import {
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent
} from "react";
import {
  normalizeRoomCode,
  ROOM_CODE_LENGTH
} from "../../../../packages/session-code/src";

interface RoomCodeInputProps {
  readonly label: string;
  readonly disabled?: boolean;
  readonly onChange: (code: string) => void;
  readonly onComplete?: (code: string) => void;
}

/**
 * Six single-character boxes, like a verification-code field: type left to
 * right with auto-advance, backspace to step back, and paste the whole code at
 * once. Every entered character is folded through the room-code normalizer so
 * lowercase and confusable characters land correctly.
 */
export function RoomCodeInput({
  label,
  disabled = false,
  onChange,
  onComplete
}: RoomCodeInputProps) {
  const [slots, setSlots] = useState<string[]>(() =>
    Array<string>(ROOM_CODE_LENGTH).fill("")
  );
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  const focusBox = (index: number) => {
    const clamped = Math.max(0, Math.min(ROOM_CODE_LENGTH - 1, index));
    const element = refs.current[clamped];
    element?.focus();
    element?.select();
  };

  const commit = (next: string[]) => {
    setSlots(next);
    const joined = next.join("");
    onChange(joined);
    if (joined.length === ROOM_CODE_LENGTH && next.every((c) => c !== "")) {
      onComplete?.(joined);
    }
  };

  const handleChange = (index: number, raw: string) => {
    const char = normalizeRoomCode(raw).slice(-1);
    if (char === "") {
      return;
    }
    const next = [...slots];
    next[index] = char;
    commit(next);
    focusBox(index + 1);
  };

  const handleKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      const next = [...slots];
      if (next[index] !== "") {
        next[index] = "";
        commit(next);
      } else {
        next[Math.max(0, index - 1)] = "";
        commit(next);
        focusBox(index - 1);
      }
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      focusBox(index + 1);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = normalizeRoomCode(event.clipboardData.getData("text")).slice(
      0,
      ROOM_CODE_LENGTH
    );
    const next = Array.from(
      { length: ROOM_CODE_LENGTH },
      (_, i) => pasted[i] ?? ""
    );
    commit(next);
    focusBox(pasted.length);
  };

  return (
    <div className="otp-input" role="group" aria-label={label}>
      {slots.map((value, index) => (
        <input
          aria-label={`${label} ${index + 1}`}
          autoComplete="one-time-code"
          className="otp-input__box"
          disabled={disabled}
          inputMode="text"
          key={index}
          maxLength={1}
          onChange={(event) => handleChange(index, event.target.value)}
          onFocus={(event) => event.target.select()}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          ref={(element) => {
            refs.current[index] = element;
          }}
          spellCheck={false}
          value={value}
        />
      ))}
    </div>
  );
}
